<template>
    <v-row no-gutters align="center" justify="center" v-if="isDomesticModel">
        <v-col md="12">
            <v-card class="mx-auto" tile>
                <v-card-title>
                    Override N_CLIENTES (Doméstico)
                    <v-dialog v-model="infoVisible" width="500">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" class="ml-2"
                                >mdi-information</v-icon
                            >
                        </template>
                        <v-card>
                            <v-card-title class="text-h5 grey lighten-2">
                                Override N_CLIENTES
                            </v-card-title>
                            <v-card-text>
                                Por defecto el modelo usa, para cada serie
                                (Tarifa, Provincia, Distribuidora,
                                Red/Satélite), el último dato real conocido de
                                N_CLIENTES para todos los meses futuros de la
                                predicción. Aquí puede sustituir ese valor por
                                uno distinto, serie a serie, antes de lanzar la
                                predicción. Solo se guardan las filas que
                                modifique; el resto de series sigue usando el
                                comportamiento actual.
                            </v-card-text>
                            <v-divider></v-divider>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="#004571"
                                    text
                                    @click="infoVisible = false"
                                >
                                    Cerrar
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-card-title>
                <v-alert v-if="!hasOverrideData" type="info" text class="mx-4">
                    Este modelo todavía no tiene datos de N_CLIENTES disponibles
                    para editar (se genera al entrenar el modelo, campo
                    <code>n_clientes_last_known</code> en su
                    <code>metaData.json</code>). Si el modelo se entrenó antes
                    de activarse esta funcionalidad, entrene un modelo nuevo
                    para poder usar el override.
                </v-alert>
                <template v-else>
                    <v-row class="px-4" align="center" justify="start">
                        <v-col cols="12" sm="4" md="3">
                            <v-select
                                v-model="filterTarifa"
                                :items="tarifaFilterOptions"
                                label="Tarifa"
                                dense
                                outlined
                                clearable
                            ></v-select>
                        </v-col>
                        <v-col cols="12" sm="4" md="3">
                            <v-select
                                v-model="filterProvincia"
                                :items="provinciaFilterOptions"
                                label="Provincia"
                                dense
                                outlined
                                clearable
                            ></v-select>
                        </v-col>
                        <v-col cols="12" sm="4" md="3">
                            <v-checkbox
                                v-model="onlyModified"
                                label="Solo modificadas"
                                dense
                                hide-details
                            ></v-checkbox>
                        </v-col>
                    </v-row>
                    <v-data-table
                        :headers="headers"
                        :items="filteredRows"
                        item-key="serieKey"
                        dense
                        :footer-props="{
                            itemsPerPageOptions: [10, 25, 50, -1]
                        }"
                        :items-per-page="25"
                    >
                        <template v-slot:item.PS="{ item }">
                            {{ item.PS === 1 ? 'Satélite' : 'Principal' }}
                        </template>
                        <template v-slot:item.N_CLIENTES="{ item }">
                            <v-text-field
                                v-model.number="item.N_CLIENTES"
                                type="number"
                                dense
                                hide-details
                                single-line
                                style="max-width: 120px"
                            ></v-text-field>
                        </template>
                        <template v-slot:item.acciones="{ item }">
                            <v-btn
                                icon
                                small
                                :disabled="!isModified(item)"
                                @click="resetRow(item)"
                            >
                                <v-icon small>mdi-undo</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                    <v-card-actions>
                        <span class="ml-2"
                            >{{ modifiedCount }} serie(s) modificada(s)</span
                        >
                        <v-spacer></v-spacer>
                        <v-btn
                            text
                            :disabled="modifiedCount === 0 || saving"
                            @click="resetAll"
                        >
                            Deshacer todos
                        </v-btn>
                        <v-btn
                            color="#004571"
                            class="white--text"
                            :loading="saving"
                            :disabled="modifiedCount === 0"
                            @click="saveOverride"
                        >
                            Guardar cambios N_CLIENTES
                        </v-btn>
                    </v-card-actions>
                </template>
                <v-snackbar v-model="snack" :timeout="4000" :color="snackColor">
                    {{ snackText }}
                    <template v-slot:action="{ attrs }">
                        <v-btn v-bind="attrs" text @click="snack = false">
                            Close
                        </v-btn>
                    </template>
                </v-snackbar>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';

Vue.use(VueAxios, axios);
import url_utils from '../helpers/url_utils';

export default {
    name: 'NClientesOverride',
    props: ['modelMetadata'],
    data() {
        return {
            rows: [],
            filterTarifa: null,
            filterProvincia: null,
            onlyModified: false,
            infoVisible: false,
            saving: false,
            snack: false,
            snackColor: '',
            snackText: '',
            headers: [
                { text: 'Tarifa', value: 'TARIFA' },
                { text: 'Provincia', value: 'PROVINCIA' },
                { text: 'Distribuidora', value: 'DISTRIBUIDORA' },
                { text: 'Red', value: 'PS' },
                { text: 'N_CLIENTES', value: 'N_CLIENTES', sortable: false },
                {
                    text: '',
                    value: 'acciones',
                    sortable: false,
                    align: 'center'
                }
            ]
        };
    },
    computed: {
        isDomesticModel() {
            return (
                this.modelMetadata !== null &&
                this.modelMetadata !== undefined &&
                this.modelMetadata.consumptionType !== undefined &&
                this.modelMetadata.consumptionType.toLowerCase() === 'domestico'
            );
        },
        hasOverrideData() {
            return (
                this.isDomesticModel &&
                Array.isArray(this.modelMetadata.n_clientes_last_known) &&
                this.modelMetadata.n_clientes_last_known.length > 0
            );
        },
        tarifaFilterOptions() {
            return [...new Set(this.rows.map(row => row.TARIFA))].sort();
        },
        provinciaFilterOptions() {
            return [...new Set(this.rows.map(row => row.PROVINCIA))].sort();
        },
        filteredRows() {
            return this.rows.filter(row => {
                if (
                    this.filterTarifa !== null &&
                    row.TARIFA !== this.filterTarifa
                )
                    return false;
                if (
                    this.filterProvincia !== null &&
                    row.PROVINCIA !== this.filterProvincia
                )
                    return false;
                if (this.onlyModified && !this.isModified(row)) return false;
                return true;
            });
        },
        modifiedCount() {
            return this.rows.filter(row => this.isModified(row)).length;
        }
    },
    watch: {
        modelMetadata() {
            this.buildRows();
        }
    },
    mounted() {
        this.buildRows();
    },
    methods: {
        formatTarifa(value) {
            return Number(value).toFixed(1);
        },
        buildRows() {
            if (!this.hasOverrideData) {
                this.rows = [];
                return;
            }
            this.rows = this.modelMetadata.n_clientes_last_known.map(serie => {
                const tarifa = Number(this.formatTarifa(serie.TARIFA));
                const distribuidora = parseInt(serie.DISTRIBUIDORA, 10);
                const ps = parseInt(serie.PS, 10);
                const original = parseInt(serie.N_CLIENTES, 10);
                return {
                    serieKey: [tarifa, serie.PROVINCIA, distribuidora, ps].join(
                        '|'
                    ),
                    TARIFA: tarifa,
                    PROVINCIA: serie.PROVINCIA,
                    DISTRIBUIDORA: distribuidora,
                    PS: ps,
                    N_CLIENTES: original,
                    original: original
                };
            });
            this.filterTarifa = null;
            this.filterProvincia = null;
            this.onlyModified = false;
        },
        isModified(row) {
            const val = row.N_CLIENTES;
            if (val === '' || val === null || val === undefined) return false;
            const num = Number(val);
            if (Number.isNaN(num)) return false;
            return num !== row.original;
        },
        resetRow(row) {
            row.N_CLIENTES = row.original;
        },
        resetAll() {
            this.rows.forEach(row => {
                row.N_CLIENTES = row.original;
            });
        },
        buildCsv() {
            const modifiedRows = this.rows.filter(row => this.isModified(row));
            const lines = ['TARIFA;PROVINCIA;DISTRIBUIDORA;PS;N_CLIENTES'];
            modifiedRows.forEach(row => {
                lines.push(
                    [
                        this.formatTarifa(row.TARIFA),
                        row.PROVINCIA,
                        parseInt(row.DISTRIBUIDORA, 10),
                        parseInt(row.PS, 10),
                        parseInt(row.N_CLIENTES, 10)
                    ].join(';')
                );
            });
            return lines.join('\n');
        },
        saveOverride() {
            this.saving = true;
            let token = this.$store.getters['getTokenHeader'];
            let dict_auth = {
                Authorization: token,
                'Content-Type': 'application/json'
            };
            var config = {
                method: 'post',
                url: url_utils.getNClientesOverrideUploadUrl(),
                headers: dict_auth,
                data: {
                    filename: 'n_clientes_override_dom.csv',
                    csv_content: this.buildCsv()
                }
            };
            Vue.axios(config)
                .then(() => {
                    this.snack = true;
                    this.snackColor = 'success';
                    this.snackText =
                        'Override de N_CLIENTES guardado correctamente';
                })
                .catch(() => {
                    this.snack = true;
                    this.snackColor = 'error';
                    this.snackText =
                        'Error al guardar el override de N_CLIENTES';
                })
                .finally(() => {
                    this.saving = false;
                });
        }
    }
};
</script>
